import { Input, InputNumber, Radio } from 'antd';
import OfferMultiSelect from '../CuratorOfferModal/OfferMultiSelect';
import {
  CURATOR_OFFER_GENRES,
  DEFAULT_CURATOR_RATE_PER_1K,
  MAX_CURATOR_OFFER_GENRES,
} from '../../constants/curatorOfferGenres';
import { AUDIENCE_COUNTRY_UNKNOWN_VALUE, ISO_COUNTRIES } from '../../constants/isoCountries';
import type { OfferTerms } from '../../utils/curatorOfferLibrary';

const { TextArea } = Input;

interface OfferDetailsFieldsProps {
  formData: OfferTerms;
  errors: Record<string, string | undefined>;
  touched: Record<string, boolean>;
  feedbackTouched: boolean;
  disabled?: boolean;
  onFieldChange: (field: keyof OfferTerms, value: unknown) => void;
  onCountryChange: (values: string[]) => void;
  onFeedbackTouched: () => void;
  onBlur: (field: string) => void;
}

const OfferDetailsFields = ({
  formData,
  errors,
  touched,
  feedbackTouched,
  disabled = false,
  onFieldChange,
  onCountryChange,
  onFeedbackTouched,
  onBlur,
}: OfferDetailsFieldsProps) => {
  const countrySelectOptions = [
    { value: AUDIENCE_COUNTRY_UNKNOWN_VALUE, label: "I don't know" },
    ...ISO_COUNTRIES.map(country => ({
      value: country.code,
      label: country.name,
    })),
  ];

  const genreSelectOptions = CURATOR_OFFER_GENRES.map(genre => ({ value: genre, label: genre }));
  const countrySelectValue = formData.audienceCountriesUnknown
    ? [AUDIENCE_COUNTRY_UNKNOWN_VALUE]
    : formData.audienceCountryCodes;

  return (
    <div className="offer-details-fields offer-form-fields">
      <div className="form-section">
        <div className="form-field">
          <label htmlFor="genres">
            Genres you can work with (up to {MAX_CURATOR_OFFER_GENRES}){' '}
            <span className="required">*</span>
          </label>
          <OfferMultiSelect
            id="genres"
            options={genreSelectOptions}
            value={formData.genres}
            onChange={value => onFieldChange('genres', value)}
            onBlur={() => onBlur('genres')}
            disabled={disabled}
            error={Boolean(touched.genres && errors.genres)}
            placeholder="Select genres"
            searchPlaceholder="Search genres…"
            emptyText="No genres found"
            maxChipCount={4}
            maxSelected={MAX_CURATOR_OFFER_GENRES}
          />
          {touched.genres && errors.genres ? (
            <div className="error-message">{errors.genres}</div>
          ) : null}
        </div>
      </div>

      <div className="form-section">
        <div className="form-field">
          <label htmlFor="defaultRatePer1k">
            Default payout rate <span className="required">*</span>
          </label>
          <InputNumber
            id="defaultRatePer1k"
            value={formData.defaultRatePer1k}
            onChange={value => onFieldChange('defaultRatePer1k', value ?? null)}
            onBlur={() => onBlur('defaultRatePer1k')}
            min={0}
            step={0.01}
            style={{ width: '100%' }}
            placeholder={String(DEFAULT_CURATOR_RATE_PER_1K)}
            disabled={disabled}
            status={touched.defaultRatePer1k && errors.defaultRatePer1k ? 'error' : ''}
            addonBefore="$"
            addonAfter="/ 1K"
          />
          {touched.defaultRatePer1k && errors.defaultRatePer1k ? (
            <div className="error-message">{errors.defaultRatePer1k}</div>
          ) : null}
        </div>
      </div>

      <div className="form-section">
        <div className="form-field">
          <label>
            Interested in paid feedback on submissions? <span className="required">*</span>
          </label>
          <br />
          <Radio.Group
            value={
              typeof formData.interestedInFeedback === 'boolean'
                ? formData.interestedInFeedback
                : undefined
            }
            onChange={e => {
              onFeedbackTouched();
              onFieldChange('interestedInFeedback', e.target.value);
            }}
            disabled={disabled}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
          {feedbackTouched && errors.interestedInFeedback ? (
            <div className="error-message">{errors.interestedInFeedback}</div>
          ) : null}
        </div>
      </div>

      <div className="form-section">
        <div className="form-field">
          <label htmlFor="audienceCountries">
            Primary audience countries (up to 5) <span className="required">*</span>
          </label>
          <OfferMultiSelect
            id="audienceCountries"
            options={countrySelectOptions}
            value={countrySelectValue}
            onChange={onCountryChange}
            onBlur={() => onBlur('audienceCountries')}
            disabled={disabled}
            error={Boolean(touched.audienceCountries && errors.audienceCountries)}
            placeholder={'Select countries or "I don\'t know"'}
            searchPlaceholder="Search countries…"
            emptyText="No countries found"
            maxChipCount={4}
            maxSelected={5}
            exclusiveValue={AUDIENCE_COUNTRY_UNKNOWN_VALUE}
          />
          {touched.audienceCountries && errors.audienceCountries ? (
            <div className="error-message">{errors.audienceCountries}</div>
          ) : null}
        </div>
      </div>

      <div className="form-section">
        <h4 className="section-title">
          Daily streams per placement <span className="required">*</span>
        </h4>
        <div className="streams-row">
          <div className="form-field">
            <label htmlFor="dailyStreamsMin">Minimum</label>
            <InputNumber
              id="dailyStreamsMin"
              value={formData.dailyStreamsMin || null}
              onChange={value => onFieldChange('dailyStreamsMin', value || 0)}
              onBlur={() => onBlur('dailyStreamsMin')}
              min={0}
              style={{ width: '100%' }}
              disabled={disabled}
              status={touched.dailyStreamsMin && errors.dailyStreamsMin ? 'error' : ''}
            />
          </div>
          <div className="form-field">
            <label htmlFor="dailyStreamsMax">Maximum</label>
            <InputNumber
              id="dailyStreamsMax"
              value={formData.dailyStreamsMax || null}
              onChange={value => onFieldChange('dailyStreamsMax', value || 0)}
              onBlur={() => onBlur('dailyStreamsMax')}
              min={0}
              style={{ width: '100%' }}
              disabled={disabled}
              status={touched.dailyStreamsMax && errors.dailyStreamsMax ? 'error' : ''}
            />
          </div>
        </div>
        {(touched.dailyStreamsMin && errors.dailyStreamsMin) ||
        (touched.dailyStreamsMax && errors.dailyStreamsMax) ? (
          <div className="error-message">{errors.dailyStreamsMin || errors.dailyStreamsMax}</div>
        ) : null}
      </div>

      <div className="form-section">
        <div className="form-field">
          <label htmlFor="notes">Additional notes</label>
          <TextArea
            id="notes"
            value={formData.notes}
            onChange={e => onFieldChange('notes', e.target.value)}
            onBlur={() => onBlur('notes')}
            rows={4}
            maxLength={500}
            disabled={disabled}
            status={touched.notes && errors.notes ? 'error' : ''}
          />
          {touched.notes && errors.notes ? (
            <div className="error-message">{errors.notes}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OfferDetailsFields;
